import React, { useState, useEffect } from 'react';
import {
  Store,
  LayoutDashboard,
  ShoppingBag,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';
import { Label } from '@vaiu/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface LandingPreferenceSelectorProps {
  onClose?: () => void;
  embedded?: boolean;
}

const LandingPreferenceSelector: React.FC<LandingPreferenceSelectorProps> = ({ 
  onClose,
  embedded = false 
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPreference, setSelectedPreference] = useState<string>('');
  
  const isMerchant = user?.otherProfiles?.merchant?.businessName || 
                    user?.otherProfiles?.merchant?.username;

  useEffect(() => {
    // Load current preference
    const currentPref = localStorage.getItem('preferred-landing') || 
                       (isMerchant ? 'merchant' : 'marketplace');
    setSelectedPreference(currentPref);
  }, [isMerchant]);

  const options = [
    {
      value: 'marketplace',
      icon: ShoppingBag,
      title: 'Marketplace',
      description: 'Browse and discover merchants, see who accepts payments',
      available: true
    },
    {
      value: 'merchant',
      icon: Store,
      title: 'Merchant Tools',
      description: 'Manage your business, view analytics, and handle payments',
      available: isMerchant,
      requiresText: !isMerchant ? 'Requires merchant account' : null
    },
    {
      value: 'dashboard',
      icon: LayoutDashboard,
      title: 'Classic Dashboard',
      description: 'Traditional wallet view with balances and transactions',
      available: true
    }
  ];

  const handleSave = () => {
    localStorage.setItem('preferred-landing', selectedPreference);
    
    // Special handling for merchant preference
    if (selectedPreference === 'merchant' && isMerchant) {
      localStorage.setItem('merchant-prefer-tools', 'true');
    } else if (isMerchant) {
      localStorage.setItem('merchant-prefer-tools', 'false');
    }
    
    toast({
      title: 'Preference Saved',
      description: 'Your default landing page has been updated',
    });
    
    if (onClose) {
      onClose();
    }
  };

  const cardClass = embedded ? '' : 'max-w-2xl mx-auto';

  return (
    <Card className={cardClass}>
      <CardHeader>
        <CardTitle>Choose Your Default View</CardTitle>
        <CardDescription>
          Select what you'd like to see when you log in
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={selectedPreference}
          onValueChange={setSelectedPreference}
          className="space-y-3"
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                selectedPreference === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              } ${!option.available ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => option.available && setSelectedPreference(option.value)}
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  disabled={!option.available}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label
                    htmlFor={option.value}
                    className="flex items-center space-x-2 font-medium cursor-pointer"
                  >
                    <option.icon className="w-4 h-4" />
                    <span>{option.title}</span>
                    {selectedPreference === option.value && (
                      <Check className="w-4 h-4 text-primary ml-auto" />
                    )}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {option.description}
                  </p>
                  {option.requiresText && (
                    <p className="text-xs text-amber-600 mt-1">
                      {option.requiresText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </RadioGroup>

        <div className="flex justify-end space-x-2 mt-6">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave}>
            Save Preference
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LandingPreferenceSelector;
